import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RecoveryAction } from '../../utils/errorHandler';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastProps {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
  recoveryActions?: RecoveryAction[];
  onClose: (id: string) => void;
  persistent?: boolean;
}

export const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  duration = 5000,
  recoveryActions,
  onClose,
  persistent = false,
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!persistent && duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, persistent]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(id);
    }, 300);
  };

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-green-50 border-green-200',
          icon: 'text-green-600',
          title: 'text-green-800',
          message: 'text-green-700',
          iconPath: 'M5 13l4 4L19 7',
        };
      case 'error':
        return {
          bg: 'bg-red-50 border-red-200',
          icon: 'text-red-600',
          title: 'text-red-800',
          message: 'text-red-700',
          iconPath: 'M6 18L18 6M6 6l12 12',
        };
      case 'warning':
        return {
          bg: 'bg-yellow-50 border-yellow-200',
          icon: 'text-yellow-600',
          title: 'text-yellow-800',
          message: 'text-yellow-700',
          iconPath:
            'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z',
        };
      case 'info':
        return {
          bg: 'bg-blue-50 border-blue-200',
          icon: 'text-blue-600',
          title: 'text-blue-800',
          message: 'text-blue-700',
          iconPath: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <div
      className={`
        fixed top-4 right-4 z-50 max-w-sm w-full
        transform transition-all duration-300 ease-in-out
        ${isVisible && !isExiting ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
    >
      <div className={`rounded-lg border p-4 shadow-lg ${styles.bg}`}>
        <div className="flex items-start">
          {/* Icon */}
          <div className="flex-shrink-0">
            <svg
              className={`h-5 w-5 ${styles.icon}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={styles.iconPath}
              />
            </svg>
          </div>

          {/* Content */}
          <div className="ml-3 flex-1">
            {title && (
              <h4 className={`text-sm font-medium ${styles.title}`}>{title}</h4>
            )}
            <p className={`text-sm ${title ? 'mt-1' : ''} ${styles.message}`}>
              {message}
            </p>

            {/* Recovery Actions */}
            {recoveryActions && recoveryActions.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {recoveryActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      action.action();
                      handleClose();
                    }}
                    className={`
                      text-xs px-3 py-1 rounded-md font-medium transition-colors
                      ${
                        action.primary
                          ? `${
                              type === 'error'
                                ? 'bg-red-600 hover:bg-red-700'
                                : type === 'warning'
                                  ? 'bg-yellow-600 hover:bg-yellow-700'
                                  : type === 'success'
                                    ? 'bg-green-600 hover:bg-green-700'
                                    : 'bg-blue-600 hover:bg-blue-700'
                            } text-white`
                          : `${
                              type === 'error'
                                ? 'text-red-700 hover:bg-red-100'
                                : type === 'warning'
                                  ? 'text-yellow-700 hover:bg-yellow-100'
                                  : type === 'success'
                                    ? 'text-green-700 hover:bg-green-100'
                                    : 'text-blue-700 hover:bg-blue-100'
                            } border border-current`
                      }
                    `}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Close Button */}
          <div className="ml-4 flex-shrink-0">
            <button
              onClick={handleClose}
              className={`
                inline-flex rounded-md p-1.5 transition-colors
                ${
                  type === 'error'
                    ? 'text-red-400 hover:bg-red-100'
                    : type === 'warning'
                      ? 'text-yellow-400 hover:bg-yellow-100'
                      : type === 'success'
                        ? 'text-green-400 hover:bg-green-100'
                        : 'text-blue-400 hover:bg-blue-100'
                }
              `}
            >
              <span className="sr-only">{t('common.close')}</span>
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
